'use client'

import { useState } from 'react'
import { PublicNav } from '@/app/(public)/components/public-nav'
import { PublicFooter } from '@/app/(public)/components/public-footer'
import { Mail, Check, Sparkles, Calendar, TrendingUp } from 'lucide-react'

export default function NewsletterPage() {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!email.trim()) return
    setSubmitting(true)
    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), name: name.trim() || undefined }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        setError(data.error || 'Anmeldung fehlgeschlagen, bitte später erneut versuchen.')
        return
      }
      setSubmitted(true)
    } catch {
      setError('Netzwerkfehler. Bitte erneut versuchen.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <PublicNav />
      <main className="bg-pub-bg" style={{ fontFamily: 'var(--font-pub)' }}>
        <section className="relative overflow-hidden border-b border-pub-rule">
          <div className="absolute inset-0 -z-10 opacity-40"
            style={{ background: 'radial-gradient(60% 60% at 50% 0%, var(--color-pub-accent-bg) 0%, transparent 70%)' }} />
          <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-20 md:grid-cols-2">
            <div>
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-pub-accent">Newsletter</p>
              <h1 className="text-pub-ink font-semibold leading-[1.05] tracking-tight"
                style={{ fontSize: 'clamp(2.4rem, 4.5vw, 3.6rem)' }}>
                Praxis-Tipps für die Gastronomie.
              </h1>
              <p className="mt-6 max-w-xl text-[16px] leading-relaxed text-pub-muted">
                Einmal pro Monat: Praxis-Tipps für die Gastronomie, kuratiert aus Branchen-Studien
                und unserer Produkt-Roadmap. Kein Spam, keine Werbung, abmeldbar mit einem Klick.
              </p>

              {submitted ? (
                <div className="mt-8 rounded-2xl border border-pub-accent bg-pub-accent-bg p-6">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-pub-accent">
                      <Check className="h-5 w-5 text-white" strokeWidth={3} />
                    </div>
                    <div>
                      <p className="text-[15px] font-semibold text-pub-ink">Fast geschafft!</p>
                      <p className="mt-1 text-[13.5px] text-pub-muted">
                        Wir haben Ihnen eine Bestätigungs-Mail an <strong>{email}</strong> gesendet. Klicken Sie auf den Link darin, um den Newsletter zu abonnieren.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="mt-8 space-y-3 max-w-md">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ihr Name (optional)"
                    className="h-12 w-full rounded-full border border-pub-rule bg-pub-surface px-5 text-[14px] text-pub-ink placeholder:text-pub-muted focus:border-pub-accent focus:outline-none"
                  />
                  <div className="flex gap-2">
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="ihre@email.de"
                      className="h-12 flex-1 rounded-full border border-pub-rule bg-pub-surface px-5 text-[14px] text-pub-ink placeholder:text-pub-muted focus:border-pub-accent focus:outline-none"
                    />
                    <button type="submit" disabled={submitting}
                      className="rounded-full bg-pub-accent px-6 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-pub-accent-dark whitespace-nowrap disabled:opacity-60">
                      {submitting ? 'Wird gesendet…' : 'Abonnieren'}
                    </button>
                  </div>
                  {error && <p className="text-[12px] text-red-600">{error}</p>}
                  <p className="text-[12px] text-pub-muted">
                    Wir versenden den Newsletter monatlich. Mit der Anmeldung akzeptieren Sie unsere{' '}
                    <a href="/datenschutz" className="underline">Datenschutzerklärung</a>.
                  </p>
                </form>
              )}
            </div>

            <div className="space-y-4">
              {[
                { icon: Sparkles, title: 'Praxis statt Theorie', body: 'Konkrete Maßnahmen, die andere Restaurants getestet haben — mit Zahlen.' },
                { icon: Calendar, title: 'Saisonale Tipps', body: 'Was im Sommer-Service funktioniert, ist im Dezember anders. Jeden Monat passend zur Saison.' },
                { icon: TrendingUp, title: 'Branchen-Trends', body: 'Was sich in der DACH-Gastronomie tut — kuratiert, nicht hochgehyped.' },
                { icon: Mail, title: '5 Minuten Lesezeit', body: 'Maximal 5 Minuten pro Ausgabe. Ihre Zeit ist wertvoll.' },
              ].map((item, i) => (
                <div key={i} className="rounded-xl border border-pub-rule bg-pub-surface p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-pub-accent-bg">
                      <item.icon className="h-5 w-5 text-pub-accent" strokeWidth={1.6} />
                    </div>
                    <div>
                      <p className="text-[15px] font-semibold text-pub-ink">{item.title}</p>
                      <p className="mt-1 text-[13.5px] text-pub-muted">{item.body}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Geplante Themen — Roadmap statt fiktive Vergangenheit */}
        <section className="bg-pub-bg">
          <div className="mx-auto max-w-4xl px-6 py-20">
            <h2 className="mb-3 text-pub-ink font-semibold tracking-tight" style={{ fontSize: 'clamp(1.6rem, 3vw, 2.2rem)' }}>
              Geplante Themen
            </h2>
            <p className="mb-8 text-[14px] text-pub-muted">
              Unsere ersten Ausgaben starten bald. Diese Themen stehen in der Vorbereitung:
            </p>
            <div className="space-y-3">
              {[
                { date: 'Demnächst', title: 'No-Show-Quote senken: 3 Hebel mit Zahlen aus der Branche', read: '6 Min' },
                { date: 'Demnächst', title: 'Anzahlungen einführen: Wann lohnt sich der psychologische Switch?', read: '5 Min' },
                { date: 'Demnächst', title: 'QR-Speisekarte vs. gedruckte Karte: Was sagen die Conversion-Daten?', read: '5 Min' },
                { date: 'Demnächst', title: 'Reservierungs-Widget: Welche Felder erhöhen die Buchung?', read: '4 Min' },
              ].map((issue, i) => (
                <div key={i} className="flex items-center justify-between gap-4 rounded-xl border border-pub-rule bg-pub-surface p-5">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-pub-accent">{issue.date}</p>
                    <p className="mt-1 text-[15px] font-semibold text-pub-ink">{issue.title}</p>
                  </div>
                  <p className="text-[12px] text-pub-muted whitespace-nowrap">{issue.read}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <PublicFooter />
    </>
  )
}
